import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { authApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Car, Eye, EyeOff, Loader2 } from 'lucide-react';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please enter both username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.login({ username, password });
      login(response.data.token, response.data.user);
      toast.success('Welcome to Raftaar Fleet Management!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand Art */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#09090B] flex-col justify-center items-center p-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 border border-[#D4AF37] rounded-full" />
          <div className="absolute bottom-32 right-16 w-96 h-96 border border-[#D4AF37] rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-[#D4AF37] rounded-full" />
        </div>
        
        <div className="relative z-10 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_fleetvision-3/artifacts/x8ibh464_image.png" 
            alt="Raftaar Tours and Travels" 
            className="w-72 h-72 object-contain mx-auto mb-6"
          />
          <p className="text-zinc-400 max-w-md leading-relaxed">
            Complete control over your fleet operations. Track vehicles, drivers, 
            trips, expenses, and maximize profitability with our premium dashboard.
          </p>
          
          {/* Feature highlights */}
          <div className="mt-12 grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-[#D4AF37] tabular-nums">100%</div>
              <div className="text-sm text-zinc-500 mt-1">Fleet Control</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#D4AF37] tabular-nums">Zero</div>
              <div className="text-sm text-zinc-500 mt-1">Leakage</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#D4AF37] tabular-nums">Real</div>
              <div className="text-sm text-zinc-500 mt-1">Profitability</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_fleetvision-3/artifacts/x8ibh464_image.png" 
              alt="Raftaar Tours and Travels" 
              className="w-32 h-32 object-contain mx-auto"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Welcome back
            </h2>
            <p className="text-slate-500 mt-2">
              Sign in to access your fleet dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700 font-medium">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                data-testid="username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold text-base shadow-lg shadow-[#D4AF37]/25 transition-all duration-200"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600 text-center">
              Raftaar Tours & Travels - Premium Fleet Management
            </p>
          </div>

          <p className="text-center text-sm text-slate-500 mt-8">
            Raftaar Tours & Travels © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
